import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import ToolTip, { TooltipElementType } from "@swiggy-private/dls-web-core/dist/components/Tooltip";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
import { isEmpty } from "@swiggy-private/js-utils";
import { REWARDS_WIDGET_TEST_IDS } from "@dineout-cart-ui/helpers/TestIds";
import {
    RewardsTextContainer,
    RewardTitleRow,
    RewardExtraInfoAnchor,
    RewardExtraInfoIconTrigger,
    RewardExtraInfoDlsTooltipWrap,
    REWARD_EXTRA_INFO_DLS_TOOLTIP_CLASS,
    SubtitleContainer,
} from "./styles";

interface IRewardTitleWithExtraInfoProps {
    title: string;
    subtitle?: string;
    extraInfoText?: string;
    isBoosterActive: boolean;
}

export const RewardTitleWithExtraInfo = memo(
    ({ title, subtitle, extraInfoText, isBoosterActive }: IRewardTitleWithExtraInfoProps): React.ReactElement => {
        const [showExtraInfoTooltip, setShowExtraInfoTooltip] = useState(false);
        const extraInfoAnchorRef = useRef<HTMLDivElement>(null);
        const extraInfoTooltipWrapRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (!showExtraInfoTooltip) {
                return undefined;
            }
            const closeIfOutside = (event: MouseEvent | TouchEvent) => {
                const target = event.target as Node | null;
                if (target == null) {
                    return;
                }
                if (extraInfoAnchorRef.current?.contains(target)) {
                    return;
                }
                if (extraInfoTooltipWrapRef.current?.contains(target)) {
                    return;
                }
                if (target instanceof Element) {
                    if (target.closest(`.${REWARD_EXTRA_INFO_DLS_TOOLTIP_CLASS}`)) {
                        return;
                    }
                    if (target.closest(`[data-testid="${REWARDS_WIDGET_TEST_IDS.EXTRA_INFO_TEXT}"]`)) {
                        return;
                    }
                }
                setShowExtraInfoTooltip(false);
            };
            const onKeyDown = (event: KeyboardEvent) => {
                if (event.key === "Escape") {
                    setShowExtraInfoTooltip(false);
                }
            };
            document.addEventListener("mousedown", closeIfOutside, true);
            document.addEventListener("touchstart", closeIfOutside, true);
            document.addEventListener("keydown", onKeyDown, true);
            return () => {
                document.removeEventListener("mousedown", closeIfOutside, true);
                document.removeEventListener("touchstart", closeIfOutside, true);
                document.removeEventListener("keydown", onKeyDown, true);
            };
        }, [showExtraInfoTooltip]);

        const handleExtraInfoClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
            setShowExtraInfoTooltip((open) => !open);
        }, []);

        const handleExtraInfoTooltipClose = useCallback(() => {
            setShowExtraInfoTooltip(false);
        }, []);

        return (
            <RewardsTextContainer>
                <RewardTitleRow>
                    <Text
                        variant={isBoosterActive ? "V2.body3.neutral.medium" : "V2.body1.neutral.bold"}
                        color="text_High_Emphasis"
                        data-testid={REWARDS_WIDGET_TEST_IDS.TITLE}
                    >
                        {title}
                    </Text>
                    {!isEmpty(extraInfoText) && (
                        <RewardExtraInfoAnchor ref={extraInfoAnchorRef}>
                            <RewardExtraInfoIconTrigger
                                onClick={handleExtraInfoClick}
                                data-testid={REWARDS_WIDGET_TEST_IDS.EXTRA_INFO_ICON}
                                role="button"
                                tabIndex={0}
                                aria-expanded={showExtraInfoTooltip}
                                aria-label="More information"
                            >
                                <SpriteIconWeb
                                    iconName="info12"
                                    strokeColor={isBoosterActive ? "text_Low_Emphasis" : "text_Med_Emphasis"}
                                    aria-hidden
                                />
                            </RewardExtraInfoIconTrigger>
                            {showExtraInfoTooltip && (
                                <RewardExtraInfoDlsTooltipWrap ref={extraInfoTooltipWrapRef}>
                                    <ToolTip
                                        className={REWARD_EXTRA_INFO_DLS_TOOLTIP_CLASS}
                                        direction="down"
                                        text={extraInfoText}
                                        elementType={TooltipElementType.LEFT_ASSET_NONE}
                                        onClose={handleExtraInfoTooltipClose}
                                        testId={REWARDS_WIDGET_TEST_IDS.EXTRA_INFO_TEXT}
                                    />
                                </RewardExtraInfoDlsTooltipWrap>
                            )}
                        </RewardExtraInfoAnchor>
                    )}
                </RewardTitleRow>
                <SubtitleContainer>
                    <Text
                        variant={isBoosterActive ? "V2.overline.neutral.medium" : "V2.caption.neutral.bold"}
                        color={isBoosterActive ? "text_Low_Emphasis" : "text_Med_Emphasis"}
                        data-testid={REWARDS_WIDGET_TEST_IDS.SUBTITLE}
                    >
                        {subtitle}
                    </Text>
                </SubtitleContainer>
            </RewardsTextContainer>
        );
    },
);

RewardTitleWithExtraInfo.displayName = "RewardTitleWithExtraInfo";
